import { LatestMedicalPaFormData } from "../../../../api/getCalls/getLatestMedicalPaFormData";
import {
  deleteNestedValue,
  setNestedValue,
} from "../../../../utils/getNestedValue";
import { getParentPathBeforeLastBracket } from "../../../../utils/getParentPathBeforeLastBreacket";
import { getUpdatedFormDataWithFilledValues } from "../../../pharmaPaForm/utils/getUpdatedFormDataWithFilledValues";

/**
 * Detects whether an object key is a flattened path (e.g. `a.b.c` or
 * `a.b[0].c`) rather than a plain single-level key.
 *
 * @param key - The object key to inspect
 * @returns `true` when the key encodes a nested path via dots or `[n]` brackets
 */
const isFlattenedKey = (key: string): boolean =>
  key.includes(".") || /\[\d+\]/.test(key);

/**
 * Collapses any flattened dotted keys (e.g. `"a.b.c"`) on an object into their
 * nested equivalent (`{ a: { b: { c } } }`) and removes the flat originals.
 *
 * Legacy form-data payloads can carry both shapes for the same path; this
 * normalizes them so only the nested structure remains.
 *
 * @param obj - The object potentially containing flattened keys
 * @returns A new object with every flattened key expanded into nested form
 */
const collapseFlattenedKeys = (obj: any): any => {
  let result = { ...obj };
  for (const key of Object.keys(obj)) {
    if (isFlattenedKey(key)) {
      result = setNestedValue(result, key, result[key], true);
      delete result[key];
    }
  }
  return result;
};

/**
 * Merges the current form-context field values onto the latest saved form data,
 * producing an order-data object that only ever uses the nested object shape
 * (`{ a: { b: { c } } }`) — never flattened dotted keys.
 *
 * @param formFieldsData - Raw form-context fields keyed by dotted path
 * @param latestMedicalPaFormData - Previously persisted form JSON to build upon
 * @returns The merged order data with fresh field values applied last
 */
export const createOrderDataFromContextFormData = (
  formFieldsData: any,
  latestMedicalPaFormData: LatestMedicalPaFormData,
) => {
  const onlyFieldsDataValue =
    getUpdatedFormDataWithFilledValues(formFieldsData);

  // Normalize any legacy flat dotted keys on the base payload up front so the
  // fresh nested values written below are the ones that win.
  let data = collapseFlattenedKeys({ ...latestMedicalPaFormData });

  for (const key in onlyFieldsDataValue) {
    const parentPath = getParentPathBeforeLastBracket(key);

    if (parentPath) {
      data = deleteNestedValue(data, parentPath);
    }
  }
  for (const key in onlyFieldsDataValue) {
    data = setNestedValue(data, key, onlyFieldsDataValue[key]);
  }

  return data;
};
